import clsx from 'clsx';
import { InfoButtonOld } from 'components/InfoButton/InfoButtonOld';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

export function TextInput({
  name,
  type,
  label,
  tooltip,
  value,
  ignoreSpaces,
  autocomplete,
  autofocus,
  onChange,
  onCompositionEnd,
  warning,
  warningMessage,
  noSpecial,
  darkIcon,
  stopAutofill,
  allowNegative,
  onBlur,
  isRegisterUserEmail,
  characterLimit,
  containerClassName,
  isDisabled,
  ...props
}: {
  name: string;
  type: string;
  value?: string | number | undefined;
  label: string | any;
  tooltip?: string | React.ReactNode | undefined;
  autocomplete?: string;
  autofocus?: boolean;
  onChange?: (value: string | number | Date | any) => void;
  onCompositionEnd?: (value: string | number | Date) => void;
  warning?: boolean;
  ignoreSpaces?: boolean;
  warningMessage?: React.ReactNode;
  noSpecial?: boolean;
  darkIcon?: boolean;
  stopAutofill?: boolean;
  allowNegative?: boolean;
  [props: string]: unknown;
  isRegisterUserEmail?: boolean;
  characterLimit?: number;
  containerClassName?: string;
  isDisabled?: boolean;
  onBlur?: () => void;
}) {
  const [stateType, setState] = useState('');
  const [hasCharacterLimitError, setHasCharacterLimitError] =
    useState<boolean>(false);

  let tempType = stateType || type;
  const inputRef = useRef<HTMLInputElement | null>(null);

  function onTextChange(input: React.ChangeEvent<HTMLInputElement>) {
    let modelValue: string = input.target.value || '';
    if (characterLimit) {
      if (modelValue.length > characterLimit) {
        setHasCharacterLimitError(true);
      } else {
        setHasCharacterLimitError(false);
        return onChange?.(modelValue);
      }
    }

    if (!ignoreSpaces && type !== 'password') modelValue = modelValue.trim();
    if (onChange) {
      if (noSpecial && !input.target.value.match(/^[a-zA-Z0-9]*$/)) {
        return null;
      }
      return onChange(modelValue);
    }
  }

  function onTextCaptureEnd(input: React.CompositionEvent<HTMLInputElement>) {
    let modelValue: string = input.currentTarget.value || '';

    if (!ignoreSpaces) modelValue = modelValue.trim();

    if (onCompositionEnd) {
      if (noSpecial && !input.currentTarget.value.match(/^[a-zA-Z0-9]*$/)) {
        return null;
      }
      return onCompositionEnd(modelValue);
    }
  }

  function onNumberChange(values: NumberFormatValues) {
    let modelValue: string = values.value || '';
    modelValue = modelValue.trim();

    if (onChange) onChange(modelValue);
  }

  function keyDown(input: React.KeyboardEvent<HTMLInputElement>) {
    const modelValue: string = input.currentTarget.value || '';
    if (input.key === 'ArrowUp' || input.key === 'ArrowDown') {
      input.preventDefault();
    }

    if (characterLimit) {
      // Event fires before value is changed hence the === check
      if (modelValue.length === characterLimit) {
        setHasCharacterLimitError(true);
      } else {
        setHasCharacterLimitError(false);
      }
    }
  }

  function onPasswordEyeClick() {
    if (stateType) tempType = stateType;

    switch (tempType) {
      case 'password':
        setState('text');
        break;
      default:
        setState(type);
        break;
    }
  }

  if (stateType) tempType = stateType;

  useEffect(() => {
    if (inputRef.current && isRegisterUserEmail) inputRef?.current?.focus();
  }, [inputRef, isRegisterUserEmail]);

  return (
    <div className={containerClassName ?? 'py-4'}>
      <div
        className={clsx(
          `${type === 'password' ? 'block w-11/12' : ''} md-input-box`,
          props,
        )}>
        {type === 'number' ? (
          <NumericFormat
            data-browsing-ignore
            id={name}
            name={name}
            value={value}
            autoFocus={autofocus}
            allowLeadingZeros
            allowNegative={allowNegative}
            className={`bg-transparent md-input py-2 px-0 border-0 ${
              warning ? 'text-error' : 'text-header-text'
            }`}
            placeholder=" "
            onValueChange={values => onNumberChange(values)}
            onBlur={onBlur}
          />
        ) : (
          <>
            <input
              ref={inputRef}
              data-browsing-ignore
              id={name}
              name={name}
              type={tempType}
              maxLength={characterLimit}
              autoComplete={autocomplete}
              autoFocus={autofocus}
              value={value}
              className={`bg-transparent md-input py-2 px-0 border-0 ${
                warning ? 'text-error' : 'text-header-text'
              } ${type === 'password' ? 'relative' : ''} ${
                isDisabled ? 'cursor-not-allowed' : ''
              }`}
              placeholder=" "
              onChange={onTextChange}
              onKeyDown={e => keyDown(e)}
              onWheel={event => event.currentTarget.blur()}
              readOnly={stopAutofill}
              onFocus={e =>
                stopAutofill && e.target.removeAttribute('readonly')
              }
              onCompositionEnd={onTextCaptureEnd}
              disabled={isDisabled}
            />
          </>
        )}
        <label
          htmlFor={name}
          className={`block font-normal leading-5 md-label ${
            warning ? 'text-error' : 'text-gray-900'
          }`}>
          {!tooltip ? (
            <div className="mt-2">{label}</div>
          ) : (
            <div className="flex flex-row">
              <span className="whitespace-nowrap mr-2 pt-2">{label}</span>
              <InfoButtonOld
                // RS :: NOTE: defaulting to only use fixTooltip if darkIcon is true to ensure we exclude register flow
                fixTooltip={darkIcon}
                darkIcon={darkIcon}
                name={name}
                relative
                className="top-1.5">
                {tooltip}
              </InfoButtonOld>
            </div>
          )}
        </label>

        {type === 'password' ? (
          <button
            className={`md-password-button -mr-7 focus:outline-none absolute pb-10 border-b-2 border-input ${
              stateType === 'text' ? ' visible' : ''
            }`}
            type="button"
            onClick={onPasswordEyeClick}
          />
        ) : null}

        <div
          className={`block border-0 border-b-2 form-input p-0 rounded-b-none sm:leading-5 sm:text-sm w-full border-register-border ${
            warning ? 'border-red-500' : ''
          }`}
        />

        {characterLimit && characterLimit > 0 && (
          <CharacterCounter
            hasError={hasCharacterLimitError}
            characterCount={value?.toString()?.length as number}
            characterLimit={characterLimit as number}
          />
        )}
      </div>

      {!warning ? null : warningMessage}
    </div>
  );
}

const CharacterCounter = ({
  characterCount,
  characterLimit,
  hasError,
}: {
  characterCount: number;
  characterLimit: number;
  hasError: boolean;
}) => (
  <div className={`absolute right-4 top-2 ${hasError ? 'text-error' : ''}`}>
    {characterCount}/{characterLimit}
  </div>
);
